import {
    IAccountPersonalDetails,
    IAccountProfessionalDetails,
    IAccountPasswordChange,
    IAllAccountData,
    IAccountContactDetails,
    IAccountSummary,
    IAccountTaxDetails,
    ITOTPData,
    IAccountInvoicingDetails,
} from '@/types/types/forms/account/account.interface';
import { Config, makeFetcher } from '@/utils/fetch';
import { config } from '@/config';

const makeAccountService = (apiUrl: string) => {
    const fetcher = makeFetcher(apiUrl);

    return {
        getAccountData: async (config?: Config) => {
            const res = await fetcher.get('', config);
            const data = (await res.json()) as IAllAccountData;
            return data;
        },

        savePersonalDetails: async (data: Partial<IAccountPersonalDetails>, hubinKey?: string) => {
            const res = await fetcher.post(
                `/personal-details${hubinKey ? `?hubinKey=${hubinKey}` : ''}`,
                JSON.stringify(data),
            );

            const { surveyUrl } = (await res.json()) as { surveyUrl: string | null };

            return {
                status: res.status,
                surveyUrl: surveyUrl,
            };
        },

        updatePersonalDetails: async (data: Partial<IAccountPersonalDetails>) => {
            const res = await fetcher.patch('/personal-details', JSON.stringify(data), {
                disableRedirects: true,
            });
            return res.status;
        },

        saveProfessionalDetails: async (
            data: Partial<IAccountProfessionalDetails>,
        ): Promise<void> => {
            await fetcher.post('/professional-details', JSON.stringify(data));
        },

        saveContactDetails: async (data: Partial<IAccountContactDetails>): Promise<number> => {
            const res = await fetcher.post('/contact-details', JSON.stringify(data));
            return res.status;
        },

        sendSMSCode: async (mobilePhone: string, countryCode: string) => {
            const res = await fetcher.post(
                '/send-sms-code',
                JSON.stringify({ mobilePhone, countryCode }),
            );
            const { mobilePhoneCodeId } = (await res.json()) as { mobilePhoneCodeId: number };
            return mobilePhoneCodeId;
        },

        sendConfirmationEmail: async (source?: string): Promise<number> => {
            const res = await fetcher.post('/send-confirmation-email', JSON.stringify({ source }));
            return res.status;
        },

        sendPendingEmail: async (email: string): Promise<number> => {
            const res = await fetcher.post(
                '/pending-email',
                JSON.stringify({ pendingEmail: email }),
            );
            return res.status;
        },

        deletePendingEmail: async (): Promise<number> => {
            const res = await fetcher.delete('/pending-email');
            return res.status;
        },

        sendPasswordCheck: async (currentPassword: string) => {
            const res = await fetcher.post('/password-check', JSON.stringify({ currentPassword }));
            return res.status;
        },

        passwordChange: async (data: Partial<IAccountPasswordChange>) => {
            const res = await fetcher.post('/password-change', JSON.stringify(data));
            return res.status;
        },

        summary: async () => {
            const res = await fetcher.get('/summary', { disableRedirects: true });
            const data = (await res.json()) as IAccountSummary;
            return data;
        },

        getAccountTaxDetails: async () => {
            const res = await fetcher.get('/tax-details', { disableRedirects: true });
            const data = (await res.json()) as IAccountTaxDetails;
            return data;
        },

        saveAccountTaxDetails: async (data: Partial<IAccountTaxDetails>, hubinKey?: string) => {
            const res = await fetcher.post(
                `/tax-details${hubinKey ? `?hubinKey=${hubinKey}` : ''}`,
                JSON.stringify(data),
                {
                    disableRedirects: true,
                },
            );
            const { surveyUrl } = (await res.json()) as { surveyUrl: string | null };
            return { surveyUrl };
        },

        dismissNotification: async (assignmentId: number) => {
            await fetcher.post(`/notification/dismiss`, JSON.stringify({ assignmentId }));
        },

        getTOTPSecret: async () => {
            const res = await fetcher.get('/TOTP');
            const { totpToken } = (await res.json()) as { totpToken: string };
            return totpToken;
        },

        generateTOTPSecret: async () => {
            const res = await fetcher.post('/TOTP');
            const data = (await res.json()) as ITOTPData;
            return data;
        },

        removeTOTPSecret: async () => {
            const res = await fetcher.delete('/TOTP');
            return res.status;
        },

        getInvoicingDetails: async () => {
            const res = await fetcher.get('/user/invoicing', { disableRedirects: true });
            const data = (await res.json()) as IAccountInvoicingDetails;
            return data;
        },

        updateInvoicingDetails: async (data: Partial<IAccountInvoicingDetails>) => {
            const res = await fetcher.patch('/user/invoicing', JSON.stringify(data), {
                disableRedirects: true,
            });
            return (await res.json()) as IAccountInvoicingDetails;
        },
    };
};

export const accountService = makeAccountService(config.api.url.account);
